import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DownloadIcon from "@mui/icons-material/Download";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import LaptopMacIcon from "@mui/icons-material/LaptopMac";
import GitHubIcon from "@mui/icons-material/GitHub";
import HomeIcon from "@mui/icons-material/Home";

function DownloadContent() {
  const theme = useTheme();

  const handleAutoDownload = async (option) => {
    try {
      // Fetch latest release info from GitHub API
      const response = await fetch(
        "https://api.github.com/repos/mhome-ai/desktop-releases/releases/latest",
      );
      const releaseData = await response.json();

      // Find the Windows executable asset
      const windowsAsset = releaseData.assets.find(
        (asset) => asset.name.includes(".exe") && asset.name.includes("Setup"),
      );

      if (windowsAsset) {
        // Create a temporary link and click it to download
        const link = document.createElement("a");
        link.href = windowsAsset.browser_download_url;
        link.download = windowsAsset.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // Fallback to opening the releases page
        window.open(option.downloadUrl, "_blank");
      }
    } catch (error) {
      console.error("Error downloading latest release:", error);
      // Fallback to opening the releases page
      window.open(option.downloadUrl, "_blank");
    }
  };

  const downloadOptions = [
    {
      title: "Home Assistant Add-on",
      icon: <HomeIcon sx={{ fontSize: 48, color: "#18BCF2" }} />,
      description:
        "Install mHome as a Home Assistant add-on for seamless integration",
      downloadUrl: "https://github.com/mhome-ai/ha-addon",
      isAvailableSoon: false,
      buttonText: "Open in Github",
    },
    {
      title: "Android App",
      icon: <AndroidIcon sx={{ fontSize: 40, color: "#4CAF50" }} />,
      description: "Get the mHome app for your Android device",
      downloadUrl: "#",
      isAvailableSoon: true,
    },
    {
      title: "iOS App",
      icon: <AppleIcon sx={{ fontSize: 40, color: "#007AFF" }} />,
      description: "Download mHome for iPhone and iPad",
      downloadUrl: "#",
      isAvailableSoon: true,
    },
    {
      title: "Windows Desktop",
      icon: <DesktopWindowsIcon sx={{ fontSize: 40, color: "#0078D4" }} />,
      description: "mHome Windows client",
      downloadUrl:
        "https://github.com/mhome-ai/desktop-releases/releases/latest",
      isAvailableSoon: false,
      buttonText: "Download Latest",
      autoDownload: true,
    },
    {
      title: "macOS Desktop",
      icon: <LaptopMacIcon sx={{ fontSize: 40, color: "#000" }} />,
      description: "mHome macOS client",
      downloadUrl: "#",
      isAvailableSoon: true,
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.palette.background.gradient,
        pt: { xs: 12, md: 16 },
        pb: { xs: 8, md: 12 },
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ textAlign: "center", mb: 8 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
              fontWeight: "bold",
              color: theme.palette.text.primary,
              mb: 4,
              lineHeight: 1.1,
            }}
          >
            Download{" "}
            <Box
              component="span"
              sx={{
                background: theme.palette.brand.gradientText,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }}
            >
              mHome
            </Box>{" "}
            App
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "800px",
              mx: "auto",
              lineHeight: 1.6,
              mb: 6,
            }}
          >
            Access mHome on all your devices and take control of your smart home
          </Typography>
        </Box>
      </Container>

      {/* Download Options */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={4}>
          {downloadOptions.map((option, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: "100%",
                  background: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.border.light}`,
                  borderRadius: 3,
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: theme.shadows[8],
                  },
                  position: "relative",
                  overflow: "visible",
                }}
              >
                {option.isAvailableSoon && (
                  <Chip
                    label="Available Soon"
                    sx={{
                      position: "absolute",
                      top: -12,
                      right: 16,
                      fontWeight: 600,
                      backgroundColor: theme.palette.warning.main,
                      color: theme.palette.warning.contrastText,
                    }}
                  />
                )}
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    {option.icon}
                    <Box sx={{ ml: 2 }}>
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      >
                        {option.title}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    startIcon={
                      option.buttonText === "Open in Github" ? (
                        <GitHubIcon />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    disabled={option.isAvailableSoon}
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                    onClick={
                      option.autoDownload
                        ? () => handleAutoDownload(option)
                        : undefined
                    }
                    href={!option.autoDownload ? option.downloadUrl : undefined}
                    target={
                      option.buttonText === "Open in Github"
                        ? "_blank"
                        : "_self"
                    }
                    rel={
                      option.buttonText === "Open in Github"
                        ? "noopener noreferrer"
                        : undefined
                    }
                  >
                    {option.buttonText ||
                      (option.isAvailableSoon ? "Coming Soon" : "Download Now")}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}

export default DownloadContent;
